import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { fuseAnimations } from '@fuse/animations';
import { Referral, ReferralComment } from 'app/shared/models/referral';
import { Subject, SubjectUserSecurity } from 'app/shared/models/subject';
import { AuthService } from 'app/shared/services/auth.service';
import { EventService } from 'app/shared/services/event.service';
import { UserService } from 'app/shared/services/user.service';
import {
  TranslationService,
  Language,
} from 'app/shared/services/translation.service';
import DOMPurify from 'dompurify';
import { environment } from 'environments/environment';
import { SubjectService } from 'app/shared/services/subject.service';

@Component({
  selector: 'app-referral-comments-widget',
  templateUrl: './referral-comments-widget.component.html',
  styleUrls: ['./referral-comments-widget.component.scss'],
  animations: fuseAnimations,
})
export class ReferralCommentsWidgetComponent implements OnInit {
  _subject: Subject;
  title = '';
  data = [];
  form: UntypedFormGroup;
  currentUserName: string;
  readOnly = false;
  hasSubjectUserEditPrivs = false;
  showAddPrivateMessage = false;

  language: Language;
  showTranslation = false;
  referrerName:string="";

  //added empty inputs to remove errors - YA
  @Input() id: Number;
  @Input() subjectId: Number;
  @Input() processTypeId: Number;
  @Input() settings: any;

  @Input() set subject(val: Subject) {
    if (val) {
      this.update(val);
    }
  }

  public get showTranslationControls(): boolean {
    var rc=this.referral?.referralSource 
          && ((this.referral?.referralSource.name === 'File a Concern Website' ) 
              || (this.referral?.referralSource.name === 'ReportAFraud Website' && environment.enableRAFTranslate) 
              || (this.referral?.referralSource.name === 'Freedom to Speak Up Website' && environment.enableFTSUTranslate) 
              )
          && this.referral?.reportJson;
    return rc;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private eventService: EventService,
    private userService: UserService,
    private authService: AuthService,
    private translationService: TranslationService,
    private subjectService: SubjectService,
  ) {
    this.readOnly = this.authService.isUserReadOnly();
    this.subjectService
      .getSubjectUserSecurity(this._subject, this.userService.getCurrentUserId())
      .subscribe((result: SubjectUserSecurity) => {
        if (!result || !result.restrictAccess || result.canEdit) {
          this.hasSubjectUserEditPrivs = true;
        }
      });
  
    this.language = localStorage.getItem('preferredLangComments')
      ? JSON.parse(localStorage.getItem('preferredLangComments'))
      : this.translationService.defaultLanguage;
    this.showTranslation = localStorage.getItem('showTranslationComments')
      ? JSON.parse(localStorage.getItem('showTranslationComments'))?.showTranslationComments
      : this.translationService.defaultLanguage;
  }

  ngOnInit() {    
    if (this.referral?.referralSource?.name=="File a Concern Website")    
    {
      this.title = 'Communication with whistleblower';
      this.referrerName="Whistle-blower";
    }
    else
    {
      this.title ='Communication with referrer';
      this.referrerName="Referrer";
    }

    this.form = this.fb.group({
      message: [''],
    });
  }

  get referral(): Referral {
    return this._subject &&
      this._subject.subjectReferrals &&
      this._subject.subjectReferrals.length > 0 &&
      this._subject.subjectReferrals[0].referral
      ? this._subject.subjectReferrals[0].referral
      : null;
  }
  

  update(subject?: Subject) {
    subject = subject || this._subject;
    if (subject) {
      this._subject = subject;
    }
    const referral = this.referral;
    if (referral) {
      const comments = referral.referralComments;
      this.data = comments ? [...comments] : [];
      this.data.map((comment) => {
        comment.userName = comment.addedBy || 'System';
        this.sortFileNames(comment);
      });
      this.data.sort(
        (a, b) =>
          new Date(a.dateTimeAdded).valueOf() -
          new Date(b.dateTimeAdded).valueOf()
      );
    }
  }

  addMessage(isPrivate: boolean) {    
    const val = this.form.getRawValue();
    if (val.message) {
      let msg = DOMPurify.sanitize(val.message)
        .replaceAll('</div>', '')
        .replaceAll('<div>', '\r\n')
        .replaceAll('&nbsp;', ' ');
      msg = new DOMParser().parseFromString(msg, 'text/html').body.textContent;
      // Get user's name
      if (!this.currentUserName) {
        const currentUser = this.userService.getCurrentUser();
        this.currentUserName =
          currentUser.firstName + ' ' + currentUser.lastName;
      }

      const referralComment = new ReferralComment({
        addedBy: this.currentUserName,
        comment: msg,
        private: isPrivate,
      });

      // add referral comment to referral associated with the current subject
      const referral = this._subject.subjectReferrals[0].referral;
      referral.referralComments = referral.referralComments || [];
      referral.referralComments.push(referralComment);

      // notify listeners of change to subject - no message as we just want to refresh the subject
      this.eventService.markSubjectAsDirty(this._subject.id);

      // we don't need to do this but to improve user experience add to the list now before waiting for the refresh...
      this.data.push(
        new ReferralComment({
          dateTimeAdded: Date.now().toString(),
          addedBy: this.currentUserName,
          comment: msg,
          private: isPrivate,
        })
      );
      this.sortFileNames(this.data[this.data.length - 1]);
      this.form.controls.message.setValue('');
      this.form.markAsPristine();
    }
  }

  sortFileNames(msg: any) {
    if (msg.comment.indexOf('\n') !== -1) {
      msg.fileNames = msg.comment.slice(msg.comment.indexOf('\n'));
    }
    return msg;
  }

  onLanguageChange(language: Language) {
    this.language = language;
    localStorage.setItem('preferredLang', JSON.stringify(language));
  }

  onShowTranslationChange(showTranslation: boolean) {
    this.showTranslation = showTranslation;
    localStorage.setItem(
      'showTranslationComments',
      JSON.stringify({ showTranslationComments: showTranslation })
    );
  }

  returnMsg(msg: string) {
    return msg.indexOf('\n') === -1 ? msg : msg.slice(0, msg.indexOf('\n'));
  }
}
